import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config/useConfig";
import { useCallback, useEffect, useState } from "react";
import loadPlayerLib from "./playerLibLoader";

/**
 * A hook that loads the Kaltura player library for a given UIconf ID.
 * It handles the asynchronous loading of the player library and provides a status flag.
 *
 * @param uiconfId The UIconf ID of the player to load.
 * @returns An object containing a boolean `libLoaded` which is true if the player library has been loaded, false otherwise.
 */
export const usePlayerLib = (uiconfId: string) => {
    const config = useConfig();

    const [libLoaded, setLibLoaded] = useState(false);

    const loadPlayer = useCallback(
        (uiconfId: string, onSuccess: () => void) => {
            const libUrl = `${config.application.serviceUrl}/p/${config.application.partnerId}/embedPlaykitJs/uiconf_id/${uiconfId}`;
            // This flag is used to prevent the onSuccess callback from being called if the component has been unmounted
            // before the library has finished loading. It is part of a closure within the loadPlayer function.
            let isCancelled = false;
            loadPlayerLib(
                libUrl,
                () => {
                    // Only call the success callback if the operation has not been cancelled.
                    if (!isCancelled) {
                        onSuccess();
                    }
                }
            );
            // This function is returned to the caller of loadPlayer.
            // In the useEffect hook below, this becomes the cleanup function.
            return () => {
                isCancelled = true;
            };
        },
        [config]
    );

    useEffect(() => {
        // The loadPlayer function is called, and it returns a cleanup function.
        // React will execute this cleanup function when the component unmounts.
        // This is the key to preventing state updates on unmounted components.
        return loadPlayer(uiconfId, () => setLibLoaded(true));
    }, [loadPlayer, uiconfId]);

    return { libLoaded };
};
